/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `nap_event` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class NapEventMesg : Mesg {
    public constructor() : super("NapEvent", Profile.MesgNum.NAP_EVENT)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** timestamp. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** start_time, in seconds. */
    public var startTime: Instant?
        get() = (getFieldValue(START_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(START_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** start_timezone_offset, in minutes. */
    public var startTimezoneOffset: Short?
        get() = getFieldValue(START_TIMEZONE_OFFSET_FIELD_NUM) as? Short
        set(value) = setFieldValue(START_TIMEZONE_OFFSET_FIELD_NUM, value)

    /** end_time, in seconds. */
    public var endTime: Instant?
        get() = (getFieldValue(END_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(END_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** end_timezone_offset, in minutes. */
    public var endTimezoneOffset: Short?
        get() = getFieldValue(END_TIMEZONE_OFFSET_FIELD_NUM) as? Short
        set(value) = setFieldValue(END_TIMEZONE_OFFSET_FIELD_NUM, value)

    /** feedback. */
    public var feedback: NapPeriodFeedback?
        get() = (getFieldValue(FEEDBACK_FIELD_NUM) as? UByte)?.let { NapPeriodFeedback.fromValue(it) }
        set(value) = setFieldValue(FEEDBACK_FIELD_NUM, value?.value)

    /** is_deleted. */
    public var isDeleted: Boolean?
        get() = (getFieldValue(IS_DELETED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(IS_DELETED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** source. */
    public var source: NapSource?
        get() = (getFieldValue(SOURCE_FIELD_NUM) as? UByte)?.let { NapSource.fromValue(it) }
        set(value) = setFieldValue(SOURCE_FIELD_NUM, value?.value)

    /** update_timestamp. The timestamp representing when this nap event was last updated */
    public var updateTimestamp: Instant?
        get() = (getFieldValue(UPDATE_TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(UPDATE_TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val START_TIME_FIELD_NUM: UByte = 0u
        public const val START_TIMEZONE_OFFSET_FIELD_NUM: UByte = 1u
        public const val END_TIME_FIELD_NUM: UByte = 2u
        public const val END_TIMEZONE_OFFSET_FIELD_NUM: UByte = 3u
        public const val FEEDBACK_FIELD_NUM: UByte = 4u
        public const val IS_DELETED_FIELD_NUM: UByte = 5u
        public const val SOURCE_FIELD_NUM: UByte = 6u
        public const val UPDATE_TIMESTAMP_FIELD_NUM: UByte = 7u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[0] = Field("StartTime", 0u, BaseType.UINT32, 1.0, 0.0, "seconds", false)
            fields[1] = Field("StartTimezoneOffset", 1u, BaseType.SINT16, 1.0, 0.0, "minutes", false)
            fields[2] = Field("EndTime", 2u, BaseType.UINT32, 1.0, 0.0, "seconds", false)
            fields[3] = Field("EndTimezoneOffset", 3u, BaseType.SINT16, 1.0, 0.0, "minutes", false)
            fields[4] = Field("Feedback", 4u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[5] = Field("IsDeleted", 5u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[6] = Field("Source", 6u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[7] = Field("UpdateTimestamp", 7u, BaseType.UINT32, 1.0, 0.0, "", false)
            return fields
        }
    }
}
